import axiosInstance from "../helpers/axios";
import { notificationConstants as C } from "./constants";
import { getSocket, joinSocketUser } from "../socket/socket";
import {
    playDing,
    speak,
    initNotifyAudioUnlockListeners,
} from "../utils/notifyAudio";
import {
    isChatContextVisible,
    isChatMessageActuallySeen,
    isPageEngaged,
} from "../utils/chatVisibility";

let userHandler = null;
let officeHandler = null;
let readHandler = null;
let readAllHandler = null;
let connectUserHandler = null;
let connectOfficeHandler = null;
let audioListenersInitialized = false;

const processedRealtimeIds = new Set();

const SETTINGS_KEYS = {
    sound: "ao_notify_sound_enabled",
    chatSound: "ao_notify_chat_sound_enabled",
};

const officeMemberEvents = [
    "office_invite_created",
    "office_invite_accepted",
    "office_invite_declined",
    "office_member_updated",
    "office_member_removed",
];

const getStoredBool = (key, fallback = true) => {
    try {
        const value = localStorage.getItem(key);

        if (value === null) return fallback;

        return value !== "false";
    } catch (_error) {
        return fallback;
    }
};

const isChatNotification = (notification) => {
    const type = notification?.type || "";
    const event = notification?.meta?.event || "";

    return type === "chat" || event === "chat_message";
};

const canPlayNotificationSound = (notification) => {
    const soundEnabled = getStoredBool(SETTINGS_KEYS.sound, true);

    if (!soundEnabled) return false;

    if (isChatNotification(notification)) {
        return getStoredBool(SETTINGS_KEYS.chatSound, true);
    }

    return true;
};

const rememberRealtimeId = (id) => {
    if (!id) return false;

    const key = String(id);

    if (processedRealtimeIds.has(key)) return true;

    processedRealtimeIds.add(key);

    if (processedRealtimeIds.size > 500) {
        const first = processedRealtimeIds.values().next().value;
        processedRealtimeIds.delete(first);
    }

    return false;
};

const ensureAudioUnlock = () => {
    if (audioListenersInitialized) return;

    audioListenersInitialized = true;
    initNotifyAudioUnlockListeners();
};

const getCurrentUserId = (getState) => {
    const stateUser = getState?.()?.auth?.user;

    if (stateUser?._id || stateUser?.id) {
        return String(stateUser._id || stateUser.id);
    }

    try {
        const raw = localStorage.getItem("user");

        if (!raw) return null;

        const user = JSON.parse(raw);

        return user?._id || user?.id ? String(user._id || user.id) : null;
    } catch (_error) {
        return null;
    }
};

const normalizeNotification = (raw) => {
    const notification = raw?.notification ?? raw ?? {};
    const scope = notification.scope || notification.meta?.scope || "personal";
    const id = notification._id ?? notification.id;

    return {
        _id: id ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        id: notification.id ?? notification._id ?? id,
        title: notification.title ?? "Уведомление",
        message: notification.message ?? "",
        createdAt: notification.createdAt ? new Date(notification.createdAt) : new Date(),
        isRead: !!notification.isRead,
        link: notification.link ?? "",
        type: notification.type ?? "system",
        scope,
        user: notification.user ?? undefined,
        office: notification.office ?? notification.officeId ?? notification.meta?.officeId ?? null,
        meta: notification.meta ?? {},
        recipients: notification.recipients || [],
        readBy: notification.readBy || [],
    };
};

const getNotificationId = (notification) => {
    return notification?._id || notification?.id || null;
};

const getNotificationOfficeId = (notification) => String(
    notification?.office?._id ||
    notification?.office?.id ||
    notification?.office ||
    notification?.officeId ||
    notification?.meta?.officeId ||
    ""
).trim();

const isNotificationAllowedForSubscription = (notification, {
    scope = "any",
    officeId = null,
} = {}) => {
    const notificationScope = notification?.scope === "office" ? "office" : "personal";

    if (scope === "personal") {
        return notificationScope === "personal";
    }

    if (scope === "office") {
        if (notificationScope !== "office") return false;

        const expectedOfficeId = String(officeId || "").trim();
        if (!expectedOfficeId || expectedOfficeId === "all") return true;

        const notificationOfficeId = getNotificationOfficeId(notification);
        return notificationOfficeId === expectedOfficeId;
    }

    return true;
};

const isEventForCurrentUser = (data = {}, getState) => {
    if (!data.userId) return true;

    const currentUserId = getCurrentUserId(getState);

    if (!currentUserId) return false;

    return String(data.userId) === String(currentUserId);
};

const getNotificationChatId = (notification) => {
    return notification?.meta?.chatId ||
        notification?.meta?.targetChatId ||
        notification?.meta?.targetId ||
        notification?.meta?.chat ||
        notification?.meta?.roomId ||
        notification?.chatId ||
        notification?.targetChatId ||
        notification?.targetId ||
        null;
};

const isNotificationForActiveChat = (notification, _getState) => {
    if (!isChatNotification(notification)) return false;

    const notificationChatId = getNotificationChatId(notification);
    if (!notificationChatId) return false;

    // Источник истины — реально смонтированный и видимый ChatCore, а не один
    // Redux activeChatId. Это позволяет одновременно видеть основной чат и
    // глобальный мини-чат другого офиса без ложных toast/unread.
    return isChatContextVisible(notificationChatId);
};

const markRealtimeNotificationSilently = async (notification) => {
    const id = getNotificationId(notification);

    if (!id) return;

    try {
        await axiosInstance.put(`/notification/read/${id}`, null, {
            params: {
                scope: notification.scope || undefined,
                officeId: notification.office || notification.meta?.officeId || undefined,
            },
        });
    } catch (_error) { }
};

const pushToastToWindowQueue = (notification) => {
    if (typeof window === "undefined") return;

    if (!window.__notificationToastQueue) {
        window.__notificationToastQueue = [];
    }

    const id = notification._id || notification.id;

    const exists = window.__notificationToastQueue.some((item) => (
        String(item?._id || item?.id) === String(id)
    ));

    if (!exists) {
        window.__notificationToastQueue.push(notification);
    }

    if (window.__notificationToastQueue.length > 50) {
        window.__notificationToastQueue = window.__notificationToastQueue.slice(-50);
    }
};

const dispatchToastEvents = (notification) => {
    if (typeof window === "undefined") return;

    try {
        window.dispatchEvent(new CustomEvent("notification-toast", {
            detail: notification,
        }));
    } catch (_error) { }

    try {
        const toastEventName = notification.scope === "office" ? "office-toast" : "user-toast";

        window.dispatchEvent(new CustomEvent(toastEventName, {
            detail: notification,
        }));
    } catch (_error) { }
};

const notifyRealtime = async (notification) => {
    if (!notification) return;

    pushToastToWindowQueue(notification);
    dispatchToastEvents(notification);

    if (canPlayNotificationSound(notification)) {
        const audioPlayed = await playDing();

        if (!audioPlayed) {
            speak("Новое уведомление");
        }
    }

    if (typeof window === "undefined") return;
    if (typeof document === "undefined") return;
    if (isPageEngaged()) return;
    if (!("Notification" in window)) return;

    const title = notification.title || "Новое уведомление";
    const body = notification.message || "";

    if (Notification.permission === "granted") {
        new Notification(title, { body, icon: "/favicon.ico" });
        return;
    }

    if (Notification.permission !== "denied") {
        Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
                new Notification(title, { body, icon: "/favicon.ico" });
            }
        });
    }
};

const emitOfficeMembersChanged = (notification) => {
    if (!officeMemberEvents.includes(notification?.meta?.event)) return;

    try {
        window.dispatchEvent(new CustomEvent("office-members:changed", {
            detail: notification,
        }));
    } catch (_error) { }
};

const createHandler = (dispatch, getState, subscription = {}) => (data) => {
    const notification = normalizeNotification(data);
    const notificationId = getNotificationId(notification);

    if (!notification.title && !notification.message) return;
    if (!isNotificationAllowedForSubscription(notification, subscription)) return;

    if (rememberRealtimeId(notificationId)) return;

    const activeChatNotification = isNotificationForActiveChat(notification, getState);
    const notificationChatId = getNotificationChatId(notification);
    const activeChatMessageSeen = activeChatNotification &&
        isChatMessageActuallySeen(notificationChatId);

    if (activeChatNotification) {
        // Если текущий чат реально открыт на видимой вкладке, отдельный toast
        // не нужен. Когда пользователь находится у нижней границы и сообщение
        // действительно видно, сразу кладём notification в Redux как read —
        // тогда badge даже кратковременно не прыгает вверх, пока BACK принимает
        // mark-read. Если пользователь ушёл вверх по истории, notification
        // остаётся unread, но popup всё равно не мешает чтению текущего чата.
        dispatch({
            type: C.RECEIVED,
            payload: {
                ...notification,
                isRead: activeChatMessageSeen ? true : notification.isRead,
                __suppressToast: true,
            },
        });

        if (activeChatMessageSeen) {
            markRealtimeNotificationSilently(notification);
        }
        return;
    }

    dispatch({
        type: C.RECEIVED,
        payload: notification,
    });

    emitOfficeMembersChanged(notification);
    notifyRealtime(notification);
};

const isReadEventAllowedForOffice = (data = {}, officeId = null) => {
    const scope = data.scope === "office" ? "office" : "personal";

    if (scope !== "office") return true;

    const expectedOfficeId = String(officeId || "").trim();
    if (!expectedOfficeId || expectedOfficeId === "all") return true;

    const eventOfficeId = String(data.officeId || "").trim();
    return eventOfficeId === expectedOfficeId;
};

const createReadHandler = (dispatch, getState, officeId = null) => (data = {}) => {
    if (!isEventForCurrentUser(data, getState)) return;
    if (!isReadEventAllowedForOffice(data, officeId)) return;

    const id = data.id || data._id || data.notificationId;

    if (!id) return;

    dispatch({
        type: C.NOTIFICATION_REMOTE_READ,
        payload: {
            id,
            scope: data.scope || "personal",
            officeId: data.officeId || null,
            userId: data.userId || null,
        },
    });
};

const createReadAllHandler = (dispatch, getState, officeId = null) => (data = {}) => {
    if (!isEventForCurrentUser(data, getState)) return;
    if (!isReadEventAllowedForOffice(data, officeId)) return;

    dispatch({
        type: C.NOTIFICATION_REMOTE_READ_ALL,
        payload: {
            scope: data.scope || "personal",
            officeId: data.officeId || null,
            userId: data.userId || null,
        },
    });
};

const detachNotificationSocketHandlers = (socket) => {
    if (!socket) return;

    if (connectUserHandler) {
        socket.off("connect", connectUserHandler);
        connectUserHandler = null;
    }

    if (connectOfficeHandler) {
        socket.off("connect", connectOfficeHandler);
        connectOfficeHandler = null;
    }

    if (userHandler) {
        socket.off("notification:new", userHandler);
        userHandler = null;
    }

    if (officeHandler) {
        socket.off("office:notification:new", officeHandler);
        officeHandler = null;
    }

    if (readHandler) {
        socket.off("notification:read", readHandler);
        readHandler = null;
    }

    if (readAllHandler) {
        socket.off("notification:read-all", readAllHandler);
        readAllHandler = null;
    }
};

export const loadNotificationCounts = ({ officeId = null } = {}) => async (dispatch) => {
    try {
        dispatch({ type: C.FETCH_COUNTS_REQUEST });

        const res = await axiosInstance.get("/notification/counts", {
            params: {
                officeId: officeId || undefined,
            },
        });

        if (res.status === 200) {
            dispatch({
                type: C.FETCH_COUNTS_SUCCESS,
                payload: {
                    personal: Number(res.data?.personal || 0),
                    office: Number(res.data?.office || 0),
                    officeTotal: Number(res.data?.officeTotal ?? res.data?.office ?? 0),
                    byOffice: res.data?.byOffice && typeof res.data.byOffice === "object"
                        ? res.data.byOffice
                        : {},
                    total: Number(res.data?.total || 0),
                },
            });

            return res.data;
        }

        dispatch({
            type: C.FETCH_COUNTS_FAILURE,
            payload: {
                error: "Не удалось загрузить количество уведомлений",
            },
        });

        return null;
    } catch (error) {
        dispatch({
            type: C.FETCH_COUNTS_FAILURE,
            payload: {
                error: error?.response?.data?.message || error.message,
            },
        });

        return null;
    }
};

export const resetAndLoadFirstPage = (filtersPatch = {}) => async (dispatch) => {
    dispatch({
        type: C.SET_FILTERS,
        payload: filtersPatch,
    });

    dispatch({ type: C.RESET_LIST });

    await dispatch(loadNextPage());
};

export const loadNextPage = () => async (dispatch, getState) => {
    try {
        const {
            nextCursor,
            hasMore,
            filters = {},
            loading,
        } = getState().notification || {};

        if (!hasMore || loading) return null;

        const scope = filters.scope === "office" ? "office" : "personal";

        dispatch({ type: C.FETCH_PAGE_REQUEST });

        const params = {
            limit: filters.limit,
            sort: filters.sort,
            from: filters.from || undefined,
            to: filters.to || undefined,
            cursor: nextCursor || undefined,
        };

        let url = "/notification/user";

        if (scope === "office") {
            url = "/notification/office";
            if (filters.officeId && filters.officeId !== "all") {
                params.officeId = filters.officeId;
            }
        }

        const res = await axiosInstance.get(url, { params });

        if (res.status === 200) {
            const { notifications, paging, unread } = res.data;

            dispatch({
                type: C.FETCH_PAGE_SUCCESS,
                payload: {
                    scope,
                    items: notifications || [],
                    nextCursor: paging?.nextCursor || null,
                    hasMore: !!paging?.hasMore,
                    unread,
                },
            });

            return res.data;
        }

        dispatch({
            type: C.FETCH_PAGE_FAILURE,
            payload: {
                error: "Не удалось загрузить уведомления",
            },
        });

        return null;
    } catch (error) {
        dispatch({
            type: C.FETCH_PAGE_FAILURE,
            payload: {
                error: error?.response?.data?.message || error.message,
            },
        });

        return null;
    }
};

export const markNotificationRead = (id, options = {}) => async (dispatch) => {
    try {
        if (!id) return null;

        dispatch({
            type: C.NOTIFICATION_MARK_READ_REQUEST,
            payload: { id },
        });

        const res = await axiosInstance.put(`/notification/read/${id}`, null, {
            params: {
                scope: options.scope || undefined,
                officeId: options.officeId || undefined,
            },
        });

        if (res.status === 200) {
            const scope = res.data?.notification?.scope || options.scope || "personal";

            dispatch({
                type: C.NOTIFICATION_MARK_READ,
                payload: {
                    id,
                    scope,
                    officeId: res.data?.notification?.office ||
                        res.data?.notification?.meta?.officeId ||
                        options.officeId ||
                        null,
                },
            });

            return res.data;
        }

        dispatch({
            type: C.NOTIFICATION_MARK_READ_FAILURE,
            payload: {
                error: "Не удалось отметить уведомление",
            },
        });

        return null;
    } catch (error) {
        dispatch({
            type: C.NOTIFICATION_MARK_READ_FAILURE,
            payload: {
                error: error?.response?.data?.message || error.message,
            },
        });

        return null;
    }
};

export const markAllNotificationsRead = (options = {}) => async (dispatch) => {
    try {
        const scope = options.scope === "office" ? "office" : "personal";

        dispatch({
            type: C.NOTIFICATION_MARK_ALL_READ_REQUEST,
            payload: { scope },
        });

        const res = await axiosInstance.put("/notification/read-all", null, {
            params: {
                scope,
                officeId: scope === "office" ? options.officeId : undefined,
            },
        });

        if (res.status === 200) {
            dispatch({
                type: C.NOTIFICATION_MARK_ALL_READ,
                payload: {
                    scope,
                    officeId: scope === "office" ? (options.officeId || null) : null,
                },
            });

            return res.data;
        }

        dispatch({
            type: C.NOTIFICATION_MARK_ALL_READ_FAILURE,
            payload: {
                error: "Не удалось отметить уведомления",
            },
        });

        return null;
    } catch (error) {
        dispatch({
            type: C.NOTIFICATION_MARK_ALL_READ_FAILURE,
            payload: {
                error: error?.response?.data?.message || error.message,
            },
        });

        return null;
    }
};

export const subscribeUserNotifications = () => (dispatch, getState) => {
    ensureAudioUnlock();

    const socket = getSocket();
    const userId = getCurrentUserId(getState);

    if (!socket || !userId) return undefined;

    // One global owner for notification socket listeners. Office notifications
    // are delivered through verified user rooms, so switching the active office
    // must not subscribe/unsubscribe notification transport.
    detachNotificationSocketHandlers(socket);

    const localConnectUserHandler = () => {
        joinSocketUser();
        socket.emit("notification:join");

        // Reconcile counters after every reconnect in case events were created
        // while this browser was offline.
        dispatch(loadNotificationCounts());
    };

    const localUserHandler = createHandler(dispatch, getState, {
        scope: "personal",
    });
    const localOfficeHandler = createHandler(dispatch, getState, {
        scope: "office",
    });
    const localReadHandler = createReadHandler(dispatch, getState, null);
    const localReadAllHandler = createReadAllHandler(dispatch, getState, null);

    connectUserHandler = localConnectUserHandler;
    userHandler = localUserHandler;
    officeHandler = localOfficeHandler;
    readHandler = localReadHandler;
    readAllHandler = localReadAllHandler;

    socket.on("connect", localConnectUserHandler);
    socket.on("notification:new", localUserHandler);
    socket.on("office:notification:new", localOfficeHandler);
    socket.on("notification:read", localReadHandler);
    socket.on("notification:read-all", localReadAllHandler);

    if (socket.connected) {
        localConnectUserHandler();
    }

    return () => {
        socket.off("connect", localConnectUserHandler);
        socket.off("notification:new", localUserHandler);
        socket.off("office:notification:new", localOfficeHandler);
        socket.off("notification:read", localReadHandler);
        socket.off("notification:read-all", localReadAllHandler);

        if (connectUserHandler === localConnectUserHandler) connectUserHandler = null;
        if (userHandler === localUserHandler) userHandler = null;
        if (officeHandler === localOfficeHandler) officeHandler = null;
        if (readHandler === localReadHandler) readHandler = null;
        if (readAllHandler === localReadAllHandler) readAllHandler = null;
    };
};
